import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';

// Define state type
export interface ClientState {
  message: string | null;
  loading: boolean;
  error: string | null;
  clients: Client[];
}

// Define payload type for the API
export interface ClientPayload {
  id: string;
  group_id: string;
  service_name_id: string;
  client_email: string;
  client_full_name: string;
  ndis_number: string;
  email_address: string;
  phone_number: string;
  address: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  manager_email?: string;
  manager_phone?: string;
  avatar?: string;
}

// Define response type from the API
export interface ClientResponse {
  message: string;
}

// Define client interface for local state
export interface Client {
  id: string;
  clientName: string;
  ndisNumber: string;
  ndis_number?: string;
  email: string;
  phone: string;
  address: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  managerEmail?: string;
  managerPhone?: string;
  avatar?: string;
}

// Define form data interface, aligned with ClientPayload
export interface ClientInfoFormData {
  clientName: string;
  ndisNumber: string;
  email: string;
  phone: string;
  address: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  managerEmail?: string;
  managerPhone?: string;
  avatar?: string;
}

// Define props interface for ClientInfoForm component
export interface ClientInfoFormProps {
  defaultValues?: Client;
  onSubmit: (data: ClientInfoFormData) => void;
  onCancel: () => void;
  isEditMode: boolean;
}

// Define localStorage types
interface ProjectData {
  projectId?: string;
}

interface ServiceData {
  selectedServiceTitle?: string;
}

interface SelectedClientData {
  email?: string;
}

const initialState: ClientState = {
  message: null,
  loading: false,
  error: null,
  clients: []
};

export const createClient = createAsyncThunk<
  ClientResponse,
  ClientPayload,
  { rejectValue: string }
>('clientManagement/create', async (payload: ClientPayload, { rejectWithValue }) => {
  try {
    // Retrieve data from localStorage
    const project = JSON.parse(localStorage.getItem('selectedProject') || '{}') as ProjectData;
    const service = JSON.parse(localStorage.getItem('servicesState') || '{}') as ServiceData;

    console.log('Selected Project:', project);
    console.log('Selected Service:', service);

    const selectedClient = JSON.parse(
      localStorage.getItem('selectedClient') || '{}'
    ) as SelectedClientData;
    const client_email = selectedClient.email || '';

    if (!project.projectId || !service.selectedServiceTitle) {
      return rejectWithValue('Missing project, service, or client email in localStorage');
    }

    // Merge localStorage values with payload
    const updatedPayload: ClientPayload = {
      ...payload,
      group_id: project.projectId,
      service_name_id: service.selectedServiceTitle,
      client_email: client_email
    };

    const response = await axiosInstance.post<ClientResponse>(
      '/ndis/corporate-services/create-client',
      updatedPayload
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create client');
  }
});

export const updateClient = createAsyncThunk<
  ClientResponse,
  ClientPayload,
  { rejectValue: string }
>('clientManagement/update', async (payload: ClientPayload, { rejectWithValue }) => {
  try {
    // Retrieve data from localStorage
    const project = JSON.parse(localStorage.getItem('selectedProject') || '{}') as ProjectData;
    const service = JSON.parse(localStorage.getItem('servicesState') || '{}') as ServiceData;
    const selectedClient = JSON.parse(
      localStorage.getItem('selectedClient') || '{}'
    ) as SelectedClientData;
    const client_email = selectedClient.email || '';

    if (!project.projectId || !service.selectedServiceTitle || !client_email) {
      return rejectWithValue('Missing project, service, or client email in localStorage');
    }

    // Merge localStorage values with payload
    const updatedPayload: ClientPayload = {
      ...payload,
      group_id: project.projectId,
      service_name_id: service.selectedServiceTitle,
      client_email: client_email
    };

    const response = await axiosInstance.post<ClientResponse>(
      '/ndis/corporate-services/create-client',
      updatedPayload
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update client');
  }
});

export const fetchClients = createAsyncThunk<Client[], void, { rejectValue: string }>(
  'clientManagement/fetchClients',
  async (_, { rejectWithValue }) => {
    try {
      // Retrieve data from localStorage
      const project = JSON.parse(localStorage.getItem('selectedProject') || '{}') as ProjectData;
      const service = JSON.parse(localStorage.getItem('servicesState') || '{}') as ServiceData;

      if (!project.projectId || !service.selectedServiceTitle) {
        return rejectWithValue('Missing project or service in localStorage');
      }

      const response = await axiosInstance.get<{
        participants: {
          id: string;
          email: string;
          ndis_role: string;
          Client: string;
          created_date: string;
          created_by: string;
          client_full_name: string;
          phone_number: string;
          address: string;
          emergency_contact_name: string;
          emergency_contact_phone: string;
          ndis_number: string;
          manager_email?: string;
          manager_phone?: string;
          avatar?: string;
        }[];
      }>('/ndis/corporate-services/get-clients', {
        params: {
          group_id: project.projectId,
          service_name_id: service.selectedServiceTitle
        }
      });

      // Map API response to Client interface
      return response.data.participants.map((participant) => ({
        id: participant.id,
        clientName: participant.client_full_name || participant.Client,
        ndisNumber: participant.ndis_number,
        email: participant.email,
        phone: participant.phone_number,
        address: participant.address,
        emergencyContactName: participant.emergency_contact_name || undefined,
        emergencyContactPhone: participant.emergency_contact_phone || undefined,
        managerEmail: participant.manager_email || undefined,
        managerPhone: participant.manager_phone || undefined,
        avatar: participant.avatar || undefined
      }));
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
    }
  }
);

export const deleteClient = createAsyncThunk<ClientResponse, string, { rejectValue: string }>(
  'clientManagement/deleteClient',
  async (email: string, { rejectWithValue }) => {
    try {
      // Retrieve data from localStorage
      const project = JSON.parse(localStorage.getItem('selectedProject') || '{}') as ProjectData;
      const service = JSON.parse(localStorage.getItem('servicesState') || '{}') as ServiceData;

      if (!project.projectId || !service.selectedServiceTitle) {
        return rejectWithValue('Missing project or service in localStorage');
      }

      const response = await axiosInstance.get<ClientResponse>(
        '/ndis/corporate-services/delete-client',
        {
          params: {
            group_id: project.projectId,
            service_name_id: service.selectedServiceTitle,
            email: email
          }
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete client');
    }
  }
);

const clientManagementSlice = createSlice({
  name: 'clientManagement',
  initialState,
  reducers: {
    clearClientState: (state) => {
      state.message = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(createClient.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message || 'Client created successfully';
      })
      .addCase(createClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message || 'Client updated successfully';
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message || 'Client deleted successfully';
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearClientState } = clientManagementSlice.actions;
export default clientManagementSlice.reducer;
